import { authenticatedFetch, apiGet, apiPost, apiDelete } from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second, increases exponentially

export interface OpenAIStatus {
  configured: boolean;
  masked_key: string | null;
  message: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Fetch with automatic retry logic for transient failures
 */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  logto: any,
  retries: number = MAX_RETRIES
): Promise<Response> {
  try {
    const response = await authenticatedFetch(url, logto, options);

    // Retry on 5xx errors (server errors)
    if (response.status >= 500 && retries > 0) {
      const delay = RETRY_DELAY * (MAX_RETRIES - retries + 1); // Exponential backoff
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, logto, retries - 1);
    }

    return response;
  } catch (error) {
    // Retry on network errors
    if (retries > 0 && (error as Error).message.includes('fetch')) {
      const delay = RETRY_DELAY * (MAX_RETRIES - retries + 1); // Exponential backoff
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, logto, retries - 1);
    }
    throw error;
  }
}

export const settingsService = {
  /**
   * Set OpenAI API key
   */
  async setOpenAIKey(logto: any, apiKey: string): Promise<{ success: boolean; message: string }> {
    try {
      return await apiPost(`${API_BASE}/api/settings/openai-key`, logto, {
        api_key: apiKey
      });
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error setting API key',
      };
    }
  },

  /**
   * Check OpenAI configuration status
   */
  async getOpenAIStatus(logto: any): Promise<OpenAIStatus> {
    try {
      return await apiGet(`${API_BASE}/api/settings/openai-status`, logto);
    } catch (error) {
      return {
        configured: false,
        masked_key: null,
        message: 'Unable to check OpenAI status',
      };
    }
  },

  /**
   * Delete OpenAI API key
   */
  async deleteOpenAIKey(logto: any): Promise<{ success: boolean; message: string }> {
    try {
      return await apiDelete(`${API_BASE}/api/settings/openai-key`, logto);
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error deleting API key',
      };
    }
  },

  /**
   * Chat about model results with retry logic
   */
  async chatAboutModel(
    logto: any,
    message: string,
    modelResults: any,
    chatHistory: ChatMessage[] = []
  ): Promise<{ success: boolean; response?: string; message?: string; error_type?: string }> {
    try {
      const response = await fetchWithRetry(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          model_results: modelResults,
          chat_history: chatHistory,
        }),
      }, logto);

      if (!response.ok) {
        // Handle specific HTTP error codes
        if (response.status === 429) {
          return {
            success: false,
            message: 'Rate limit exceeded. Please wait a moment and try again.',
            error_type: 'rate_limit',
          };
        } else if (response.status >= 500) {
          return {
            success: false,
            message: 'Server error. Please try again in a moment.',
            error_type: 'server_error',
          };
        } else if (response.status === 400) {
          const error = await response.json().catch(() => ({}));
          return {
            success: false,
            message: error.message || error.detail || 'Invalid request',
            error_type: 'validation_error',
          };
        }

        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Chat request failed');
      }

      return await response.json();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Network error';
      return {
        success: false,
        message:
          errorMessage.includes('fetch') || errorMessage.includes('network')
            ? 'Network connection failed. Please check your internet and try again.'
            : errorMessage,
        error_type: 'network_error',
      };
    }
  },

  /**
   * Compare multiple models with retry logic
   */
  async compareModels(
    logto: any,
    models: any[],
    modelNames: string[],
    question: string,
    chatHistory: ChatMessage[] = []
  ): Promise<{ success: boolean; response?: string; message?: string; error_type?: string }> {
    try {
      const response = await fetchWithRetry(`${API_BASE}/api/chat/compare-models`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          models,
          model_names: modelNames,
          question,
          chat_history: chatHistory,
        }),
      }, logto);

      if (!response.ok) {
        // Handle specific HTTP error codes
        if (response.status === 429) {
          return {
            success: false,
            message: 'Rate limit exceeded. Please wait a moment and try again.',
            error_type: 'rate_limit',
          };
        } else if (response.status >= 500) {
          return {
            success: false,
            message: 'Server error. Please try again in a moment.',
            error_type: 'server_error',
          };
        } else if (response.status === 400) {
          const error = await response.json().catch(() => ({}));
          return {
            success: false,
            message: error.message || error.detail || 'Invalid request',
            error_type: 'validation_error',
          };
        }

        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Comparison request failed');
      }

      return await response.json();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Network error';
      return {
        success: false,
        message:
          errorMessage.includes('fetch') || errorMessage.includes('network')
            ? 'Network connection failed. Please check your internet and try again.'
            : errorMessage,
        error_type: 'network_error',
      };
    }
  },
};
